import { HttpResponse } from "msw";

import { getGetFundStructureByIdMockHandler } from "@vega-api/generated/fund-management-v1/fund-management-v1.msw";
import { getGetFundStructureV2MockHandler } from "@vega-api/generated/fund-management-v2/fund-management-v2.msw";
import {
  getCreateGaiaCharacteristicMockHandler,
  getGetGaiaCharacteristicsMockHandler,
  getUpdateGaiaCharacteristicMockHandler,
} from "@vega-api/generated/gaia-characteristics/gaia-characteristics.msw";
import {
  getCreateGaiaCompositionMockHandler,
  getGetGaiaCompositionMockHandler,
  getUpdateGaiaCompositionMockHandler,
} from "@vega-api/generated/gaia-compositions/gaia-compositions.msw";
import { getGetGaiaPerformancesMockHandler } from "@vega-api/generated/gaia-performance/gaia-performance.msw";
import { getCreateShareClassesMockHandler } from "@vega-api/generated/gaia-share-classes/gaia-share-classes.msw";
import { getGetGaiaValuationsMockHandler } from "@vega-api/generated/gaia-valuations/gaia-valuations.msw";
import { getGetLoanProgramEligibilityMockHandler } from "@vega-api/generated/loan-program-v2/loan-program-v2.msw";
import { getGetOpportunityFundStructureMockHandler } from "@vega-api/generated/opportunities-v1/opportunities-v1.msw";
import { getGetFundStructureByOpportunityIdV2MockHandler } from "@vega-api/generated/opportunities-v2/opportunities-v2.msw";
import { getGetOpportunitiesByFundV2MockHandler } from "@vega-api/generated/opportunity-v2/opportunity-v2.msw";
import { getUploadProductMediaMockHandler } from "@vega-api/generated/product-media/product-media.msw";
import { getGetTeamMembersByFundMockHandler } from "@vega-api/generated/product-team-member/product-team-member.msw";
import {
  getCreateProductMockHandler,
  getGetActiveProductMockHandler,
  getGetAllPaginatedProductsMockHandler,
  getPublishProductMockHandler,
  getUpdateProductMockHandler,
} from "@vega-api/generated/products/products.msw";
import { getGetActiveProductResponseMock } from "@vega-api/generated/products/products-mocks.msw";
import { getGetFundSchemaMockHandler } from "@vega-api/generated/schemas/schemas.msw";
import { GaiaPerformanceSummaryAssociationLevel } from "@vega-api/generated/types/gaiaPerformanceSummaryAssociationLevel";
import { GaiaPerformanceSummaryMetric } from "@vega-api/generated/types/gaiaPerformanceSummaryMetric";
import { GaiaPerformanceSummaryPeriod } from "@vega-api/generated/types/gaiaPerformanceSummaryPeriod";
import { GaiaValuationType } from "@vega-api/generated/types/gaiaValuationType";
import type { GaiaVehicleSummary } from "@vega-api/generated/types/gaiaVehicleSummary";

import { closedEndedFundData } from "./closedEndedFundData";
import { fundSchemaMockClosedEnded } from "./fundSchemaMockClosedEnded";
import { getGetFundStructureV2ResponseMock } from "./getGetFundStructureV2ResponseMock";

const FUND_ID = "closed-ended-fund-123";
const OPPORTUNITY_ID = "opportunity-456";
const defaultV2Mock = getGetFundStructureV2ResponseMock();
const defaultV2Data = defaultV2Mock.data!;
const defaultV2Vehicle = defaultV2Data.fundVehicles![0]!;

const fundHandlers = [
  getGetOpportunitiesByFundV2MockHandler({
    meta: {
      page: 1,
      pageSize: 10,
      totalPages: 4,
      count: 35,
      hasNextPage: true,
    },
    errors: null,
    data: [
      {
        id: "OPP-RCLJ-1234",
        oppName: "Redwood Investment Group",
        orgName: "Redwood Enterprises",
        orgId: "org-001",
        fundId: FUND_ID,
        createdAt: "2023-07-22T00:00:00Z",
        subdocDate: "2023-09-15T00:00:00Z",
      },
      {
        id: "OPP-KCLJ-2979",
        oppName: "Sunrise Capital Partners",
        orgName: "Nexus Strategies",
        orgId: "org-002",
        fundId: FUND_ID,
        createdAt: "2023-09-12T00:00:00Z",
        subdocDate: "2023-11-01T00:00:00Z",
      },
      {
        id: "OPP-YOSQ-6483",
        oppName: "Ocean Wave Investments",
        orgName: "Pinnacle Ventures",
        orgId: "org-003",
        fundId: FUND_ID,
        createdAt: "2023-04-18T00:00:00Z",
        subdocDate: "2023-06-30T00:00:00Z",
      },
      {
        id: "OPP-CIEG-9645",
        oppName: "Silver Lining Opportunities",
        orgName: "SilverLine Technologies",
        orgId: "org-004",
        fundId: FUND_ID,
        createdAt: "2023-11-05T00:00:00Z",
        subdocDate: "2024-01-20T00:00:00Z",
      },
      {
        id: "OPP-BZZR-2565",
        oppName: "Mountain Peak Ventures",
        orgName: "Summit Holdings",
        orgId: "org-005",
        fundId: FUND_ID,
        createdAt: "2023-06-27T00:00:00Z",
        subdocDate: "2023-08-15T00:00:00Z",
      },
      {
        id: "OPP-BZHF-8729",
        oppName: "Crystal Clear Holdings",
        orgName: "Echo Systems",
        orgId: "org-006",
        fundId: FUND_ID,
        createdAt: "2023-10-09T00:00:00Z",
        subdocDate: "2023-12-01T00:00:00Z",
      },
      {
        id: "OPP-CZIZ-0449",
        oppName: "Green Horizon Initiative",
        orgName: "GreenTech Innovations",
        orgId: "org-007",
        fundId: FUND_ID,
        createdAt: "2023-03-15T00:00:00Z",
        subdocDate: "2023-05-30T00:00:00Z",
      },
      {
        id: "OPP-SZHW-3156",
        oppName: "Starlight Development Fund",
        orgName: "Vertex Solutions",
        orgId: "org-008",
        fundId: FUND_ID,
        createdAt: "2023-02-14T00:00:00Z",
        subdocDate: "2023-04-01T00:00:00Z",
      },
      {
        id: "OPP-DLHO-6314",
        oppName: "Emerald Growth Fund",
        orgName: "Quantum Dynamics",
        orgId: "org-009",
        fundId: FUND_ID,
        createdAt: "2023-01-30T00:00:00Z",
        subdocDate: "2023-03-15T00:00:00Z",
      },
      {
        id: "OPP-GNZB-3448",
        oppName: "Blue Sky Ventures",
        orgName: "BlueWave Solutions",
        orgId: "org-010",
        fundId: FUND_ID,
        createdAt: "2023-09-22T00:00:00Z",
        subdocDate: "2023-11-10T00:00:00Z",
      },
    ],
  }),
  getGetFundStructureByOpportunityIdV2MockHandler(closedEndedFundData),
  getGetLoanProgramEligibilityMockHandler({
    data: { showLoanProgramTag: true },
    meta: {},
    errors: {},
  }),
  getGetOpportunityFundStructureMockHandler(),
  getGetFundStructureByIdMockHandler(),
  getGetFundStructureV2MockHandler({
    ...defaultV2Mock,
    data: {
      ...defaultV2Data,
      name: {
        attribute: "Apollo Global Fund",
        footnotes: undefined,
        displayNumber: 1,
      },
      fundType: "OPEN_ENDED",
      currency: { attribute: "USD", footnotes: undefined, displayNumber: 2 },
      attributes: {
        ...defaultV2Data.attributes,
        name: {
          attribute: "Apollo Global Fund",
          footnotes: [],
          displayNumber: 5,
        },
      },
      fundVehicles: [
        {
          ...defaultV2Vehicle,
          name: {
            attribute: "Apollo Global Fund Vehicle 1",
            footnotes: [],
            displayNumber: 1,
          },
          code: "AGF-V1",
          currency: { attribute: "USD", footnotes: [], displayNumber: 2 },
        },
      ],
    },
  }),
  getCreateProductMockHandler(),
  getGetActiveProductMockHandler((info) => {
    const state = new URL(info.request.url).searchParams.get("state");

    if (state === "REVISION_DRAFT") {
      return HttpResponse.json(null, { status: 404 }) as never;
    }

    return getGetActiveProductResponseMock({
      data: {
        productActiveId: "active-123",
        productId: "test-fund-123",
        state: "DRAFT",
        versionNumber: "1",
        schemaVersion: "1.0",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
        fund: {
          fundIdentifier: {
            name: "Celestial Growth Fund",
            code: "CGF-001",
          },
          investmentProductType: "CLOSED_ENDED",
          assetClass: "PRIVATE_EQUITY",
          baseCurrency: "USD",
          isRegistered: true,
          vehicles: [
            {
              vehicleId: "vehicle-001",
              fundCode: "CGF-001",
              state: "DRAFT",
              schemaVersion: "1.0",
              fundVersion: "1",
              identifier: {
                code: "VEH-001",
                name: "Vehicle 1",
              },
              shareClasses: [
                {
                  shareClassIdentifier: { code: "SC-A", name: "Share Class A" },
                },
                {
                  shareClassIdentifier: { code: "SC-B", name: "Share Class B" },
                },
              ],
            },
            {
              vehicleId: "vehicle-002",
              fundCode: "CGF-001",
              state: "DRAFT",
              schemaVersion: "1.0",
              fundVersion: "1",
              identifier: {
                code: "VEH-002",
                name: "Vehicle 2",
              },
              shareClasses: [
                {
                  shareClassIdentifier: { code: "SC-C", name: "Share Class C" },
                },
              ],
            },
            {
              vehicleId: "vehicle-003",
              fundCode: "CGF-001",
              state: "DRAFT",
              schemaVersion: "1.0",
              fundVersion: "1",
              identifier: {
                code: "VEH-003",
                name: "Vehicle 3",
              },
            },
          ] as unknown as GaiaVehicleSummary[],
        },
      },
    });
  }),
  getGetTeamMembersByFundMockHandler({
    data: {
      fundCode: "CGF-001",
      teamMembers: [
        {
          id: "00000000-0000-0000-0000-000000000001",
          name: "Marc Rowan",
          order: 1,
          role: "Co-founder and CEO of Apollo",
          biography:
            "Marc is a Co-founder and CEO of Apollo Global Management, Inc. He currently serves on the board of several companies.",
          externalLinks: undefined,
          displayImage: undefined,
        },
        {
          id: "00000000-0000-0000-0000-000000000002",
          name: "Josh Harris",
          order: 2,
          role: "Co-founder of Apollo",
          biography:
            "Josh Harris is a Co-founder of Apollo Global Management and a prominent private equity investor.",
          externalLinks: undefined,
          displayImage: undefined,
        },
        {
          id: "00000000-0000-0000-0000-000000000003",
          name: "Leon Black",
          order: 3,
          role: "Founder of Apollo",
          biography:
            "Leon Black is the Founder of Apollo Global Management, one of the world's leading alternative investment managers.",
          externalLinks: undefined,
          displayImage: undefined,
        },
      ],
      createdDate: "2025-01-01T10:00:00Z",
      schemaVersion: "1.0",
      fundVersion: "1",
    },
    meta: undefined,
    errors: undefined,
  }),
  getGetFundSchemaMockHandler(fundSchemaMockClosedEnded),
  getGetGaiaCharacteristicsMockHandler((info) => {
    const fundCode = String(info.params.fundCode ?? "");

    return {
      data: {
        fundCode,
        characteristics: [
          {
            id: "gaia-characteristic-1",
            name: "Number of Holdings",
            asOfDate: "2025-01-01",
            numericValue: 42,
            createdAt: "2025-01-01T12:00:00Z",
            updatedAt: "2025-01-02T12:00:00Z",
          },
          {
            id: "gaia-characteristic-2",
            name: "Average Position Size",
            asOfDate: "2025-01-01",
            textValue: "$238M",
            createdAt: "2025-01-01T12:00:00Z",
            updatedAt: "2025-01-02T12:00:00Z",
          },
          {
            id: "gaia-characteristic-3",
            name: "Top 10 Percentage",
            asOfDate: "2025-01-01",
            numericType: "PERCENTAGE",
            numericValue: 62,
            createdAt: "2025-01-01T12:00:00Z",
            updatedAt: "2025-01-02T12:00:00Z",
          },
          {
            id: "gaia-characteristic-4",
            name: "Average Holding Period",
            asOfDate: "2025-01-01",
            textValue: "3.2 Years",
            createdAt: "2025-01-01T12:00:00Z",
            updatedAt: "2025-01-02T12:00:00Z",
          },
        ],
        fundVersion: "1",
        schemaVersion: "1",
      },
      meta: undefined,
      errors: null,
    };
  }),
  getCreateGaiaCharacteristicMockHandler(),
  getUpdateGaiaCharacteristicMockHandler(),
  getGetGaiaPerformancesMockHandler((info) => {
    const fundCode = String(info.params.fundCode ?? "");
    const { searchParams } = new URL(info.request.url);
    const level = searchParams.get("level");
    const code = searchParams.get("code");

    const allPerformances = [
      {
        id: "1d9d55e6-29a9-4e87-9b2c-2b49cf3e8811",
        associationLevel: GaiaPerformanceSummaryAssociationLevel.FUND_LEVEL,
        asOfDate: "2026-01-14",
        metric: GaiaPerformanceSummaryMetric.INTERNAL_RATE_OF_RETURN,
        period: GaiaPerformanceSummaryPeriod.INCEPTION_TO_DATE,
        grossValue: 12.4,
        netValue: 11.9,
        createdAt: "2026-01-14T00:00:00Z",
        updatedAt: "2026-01-15T00:00:00Z",
      },
      {
        id: "1d9d55e6-29a9-4e87-9b2c-2b49cf3e8812",
        associationLevel: GaiaPerformanceSummaryAssociationLevel.FUND_LEVEL,
        asOfDate: "2026-01-14",
        metric: GaiaPerformanceSummaryMetric.MULTIPLE_ON_INVESTED_CAPITAL,
        period: GaiaPerformanceSummaryPeriod.INCEPTION_TO_DATE,
        grossValue: 2.2,
        netValue: 2.1,
        createdAt: "2026-01-14T00:00:00Z",
        updatedAt: "2026-01-15T00:00:00Z",
      },
      {
        id: "1d9d55e6-29a9-4e87-9b2c-2b49cf3e8813",
        associationLevel: GaiaPerformanceSummaryAssociationLevel.VEHICLE_LEVEL,
        vehicleCode: "VEH-001",
        asOfDate: "2026-01-14",
        metric: GaiaPerformanceSummaryMetric.INTERNAL_RATE_OF_RETURN,
        period: GaiaPerformanceSummaryPeriod.INCEPTION_TO_DATE,
        grossValue: 12.4,
        netValue: 11.9,
        createdAt: "2026-01-14T00:00:00Z",
        updatedAt: "2026-01-14T00:00:00Z",
      },
      {
        id: "1d9d55e6-29a9-4e87-9b2c-2b49cf3e8814",
        associationLevel: GaiaPerformanceSummaryAssociationLevel.VEHICLE_LEVEL,
        vehicleCode: "VEH-001",
        asOfDate: "2026-01-14",
        metric: GaiaPerformanceSummaryMetric.MULTIPLE_ON_INVESTED_CAPITAL,
        period: GaiaPerformanceSummaryPeriod.INCEPTION_TO_DATE,
        grossValue: 2.2,
        netValue: 2.1,
        createdAt: "2026-01-14T00:00:00Z",
        updatedAt: "2026-01-14T00:00:00Z",
      },
      {
        id: "1d9d55e6-29a9-4e87-9b2c-2b49cf3e8815",
        associationLevel: GaiaPerformanceSummaryAssociationLevel.VEHICLE_LEVEL,
        vehicleCode: "VEH-002",
        asOfDate: "2026-01-14",
        metric: GaiaPerformanceSummaryMetric.MULTIPLE_ON_INVESTED_CAPITAL,
        period: GaiaPerformanceSummaryPeriod.INCEPTION_TO_DATE,
        grossValue: 1.8,
        netValue: 1.6,
        createdAt: "2026-01-14T00:00:00Z",
        updatedAt: "2026-01-14T00:00:00Z",
      },
      {
        id: "1d9d55e6-29a9-4e87-9b2c-2b49cf3e8816",
        associationLevel:
          GaiaPerformanceSummaryAssociationLevel.SHARE_CLASS_LEVEL,
        vehicleCode: "VEH-001",
        shareClassCode: "SC-A",
        asOfDate: "2026-01-14",
        metric: GaiaPerformanceSummaryMetric.MULTIPLE_ON_INVESTED_CAPITAL,
        period: GaiaPerformanceSummaryPeriod.INCEPTION_TO_DATE,
        grossValue: 2.4,
        netValue: 2.2,
        createdAt: "2026-01-14T00:00:00Z",
        updatedAt: "2026-01-14T00:00:00Z",
      },
      {
        id: "1d9d55e6-29a9-4e87-9b2c-2b49cf3e8817",
        associationLevel:
          GaiaPerformanceSummaryAssociationLevel.SHARE_CLASS_LEVEL,
        vehicleCode: "VEH-001",
        shareClassCode: "SC-B",
        asOfDate: "2026-01-14",
        metric: GaiaPerformanceSummaryMetric.INTERNAL_RATE_OF_RETURN,
        period: GaiaPerformanceSummaryPeriod.INCEPTION_TO_DATE,
        grossValue: 10.8,
        netValue: 9.9,
        createdAt: "2026-01-14T00:00:00Z",
        updatedAt: "2026-01-14T00:00:00Z",
      },
      {
        id: "1d9d55e6-29a9-4e87-9b2c-2b49cf3e8818",
        associationLevel:
          GaiaPerformanceSummaryAssociationLevel.SHARE_CLASS_LEVEL,
        vehicleCode: "VEH-002",
        shareClassCode: "SC-C",
        asOfDate: "2026-01-14",
        metric: GaiaPerformanceSummaryMetric.MULTIPLE_ON_INVESTED_CAPITAL,
        period: GaiaPerformanceSummaryPeriod.INCEPTION_TO_DATE,
        grossValue: 1.9,
        netValue: 1.7,
        createdAt: "2026-01-14T00:00:00Z",
        updatedAt: "2026-01-14T00:00:00Z",
      },
    ];

    const performances = allPerformances.filter((p) => {
      if (level !== null && p.associationLevel !== level) return false;

      if (
        code !== null &&
        ("vehicleCode" in p ? p.vehicleCode : undefined) !== code &&
        ("shareClassCode" in p ? p.shareClassCode : undefined) !== code
      ) {
        return false;
      }

      return true;
    });

    return {
      data: {
        fundCode,
        fundVersion: "1",
        schemaVersion: "1",
        performances,
      },
      errors: null,
      meta: null,
    };
  }),
  getGetGaiaValuationsMockHandler((info) => {
    const fundCode = String(info.params.fundCode ?? "");

    return {
      data: {
        fundCode,
        fundVersion: "1",
        schemaVersion: "1",
        valuations: [
          {
            id: "1d9d55e6-29a9-4e87-9b2c-2b49cf3e8810",
            valuationType: GaiaValuationType.TOTAL_NET_ASSETS,
            asOfDate: "2025-01-01",
            value: 238_000_000,
            currency: "USD",
          },
        ],
      },
      meta: undefined,
      errors: null,
    };
  }),
  getGetGaiaCompositionMockHandler((info) => {
    const fundCode = String(info.params.fundCode ?? "");

    return {
      data: {
        fundCode,
        fundVersion: "1",
        schemaVersion: "1",
        compositions: [
          {
            id: "gaia-composition-1",
            name: "Sector Allocation",
            segments: [
              { segmentName: "Technology", value: 28, valueType: "PERCENTAGE" },
              { segmentName: "Healthcare", value: 22, valueType: "PERCENTAGE" },
              { segmentName: "Financials", value: 18, valueType: "PERCENTAGE" },
              {
                segmentName: "Industrials",
                value: 17,
                valueType: "PERCENTAGE",
              },
              { segmentName: "Consumer", value: 15, valueType: "PERCENTAGE" },
            ],
          },
          {
            id: "gaia-composition-2",
            name: "Geographic Exposure",
            segments: [
              {
                segmentName: "North America",
                value: 45,
                valueType: "PERCENTAGE",
              },
              { segmentName: "Europe", value: 30, valueType: "PERCENTAGE" },
              {
                segmentName: "Asia Pacific",
                value: 15,
                valueType: "PERCENTAGE",
              },
              {
                segmentName: "Latin America",
                value: 10,
                valueType: "PERCENTAGE",
              },
            ],
          },
          {
            id: "gaia-composition-3",
            name: "Investment Stage",
            segments: [
              { segmentName: "Growth", value: 40, valueType: "PERCENTAGE" },
              { segmentName: "Buyout", value: 35, valueType: "PERCENTAGE" },
              { segmentName: "Venture", value: 15, valueType: "PERCENTAGE" },
              { segmentName: "Credit", value: 10, valueType: "PERCENTAGE" },
            ],
          },
          {
            id: "gaia-composition-4",
            name: "Manager Tenure",
            segments: [
              { segmentName: "0-3 years", value: 20, valueType: "PERCENTAGE" },
              { segmentName: "3-7 years", value: 30, valueType: "PERCENTAGE" },
              { segmentName: "7-10 years", value: 25, valueType: "PERCENTAGE" },
              { segmentName: "10+ years", value: 25, valueType: "PERCENTAGE" },
            ],
          },
          {
            id: "gaia-composition-5",
            name: "Revenue Mix",
            segments: [
              { segmentName: "Recurring", value: 55, valueType: "PERCENTAGE" },
              {
                segmentName: "Transactional",
                value: 30,
                valueType: "PERCENTAGE",
              },
              {
                segmentName: "Project-based",
                value: 15,
                valueType: "PERCENTAGE",
              },
            ],
          },
          {
            id: "gaia-composition-6",
            name: "Currency Exposure",
            segments: [
              { segmentName: "USD", value: 50, valueType: "PERCENTAGE" },
              { segmentName: "EUR", value: 25, valueType: "PERCENTAGE" },
              { segmentName: "GBP", value: 15, valueType: "PERCENTAGE" },
              { segmentName: "Other", value: 10, valueType: "PERCENTAGE" },
            ],
          },
        ],
      },
      meta: undefined,
      errors: null,
    };
  }),
  getCreateGaiaCompositionMockHandler(),
  getUpdateGaiaCompositionMockHandler(),
  getCreateShareClassesMockHandler(),
  getPublishProductMockHandler(),
  getUpdateProductMockHandler(),
  getGetAllPaginatedProductsMockHandler({
    data: {
      fund: [
        {
          code: "AGF-001",
          name: "Apollo Global Fund",
          type: "OPEN_ENDED",
          assetClass: "PRIVATE_EQUITY",
          id: "1",
          updatedBy: "user",
          createdAt: "2025-01-01T10:00:00Z",
          updatedAt: "2025-01-01T10:00:00Z",
        },
        {
          code: "CGF-001",
          name: "Celestial Growth Fund",
          type: "CLOSED_ENDED",
          assetClass: "PRIVATE_EQUITY",
          id: "2",
          updatedBy: "user",
          createdAt: "2025-01-01T10:00:00Z",
          updatedAt: "2025-01-01T10:00:00Z",
        },
        {
          code: "SPF-001",
          name: "Strategic Partners Fund",
          type: "OPEN_ENDED",
          assetClass: "REAL_ASSETS",
          id: "3",
          updatedBy: "user",
          createdAt: "2025-01-01T10:00:00Z",
          updatedAt: "2025-01-01T10:00:00Z",
        },
        {
          code: "TGF-001",
          name: "Titan Growth Fund",
          type: "CLOSED_ENDED",
          assetClass: "REAL_ASSETS",
          id: "4",
          updatedBy: "user",
          createdAt: "2025-01-01T10:00:00Z",
          updatedAt: "2025-01-01T10:00:00Z",
        },
      ],
    },
  }),
  getUploadProductMediaMockHandler(),
];

export { FUND_ID, fundHandlers, OPPORTUNITY_ID };
